import {
  beginRoomRoll,
  commitRoomRoll,
  getRooms,
  getEffectiveRoll,
  resolveRoll,
  getBoardTreasureRewards,
  getBaseRoll,
  getBoardAdjustedRoll,
  getRollRelicEffects,
  getMaxHp,
} from './game';
import { getBoardRelaunchRequest } from './powerUp';
import { acquireShards } from './shardTotals';
import { needsBoardTileChoice } from './boardTileEffects';
import { getBoardRollSteps, getTriggeredBoardSquares } from './chainReaction';
import { getBoardSquareLabel, migratePlayerBoardBalance } from './doorBoard';
import { getBoardEncounterRound } from './runConstants';
import { getLastStandEncounter } from './lastStandProtocol';
import {
  createGuardian,
  GUARDIAN_PHASES,
  MINI_BOSS_PHASES,
  RIFT_WARDEN,
  getGuardianCounterDamage,
  getMiniBossIntent,
  getWardenShardReward,
} from './sectorEnemies';

export const getGuardianRoom = (game) => {
  const original = getRooms(game).find((room) => room.id === game.roomId);
  if (!original || !game.guardian) return original;
  const guardian = game.guardian;
  const intent = getMiniBossIntent({
    miniBoss: { hp: guardian.hp, round: guardian.round },
  });
  return {
    ...original,
    name: guardian.enemy.name,
    target: guardian.hp,
    ...(guardian.isWarden
      ? {
          kind: 'Boss',
          damage: intent.damage,
          shards: getWardenShardReward(guardian.round),
          heal: 0,
          bonusRelic: true,
        }
      : {
          damage: getGuardianCounterDamage(original),
          originalDamage: original.damage,
        }),
  };
};

export const getGuardianRollGame = (game) => ({
  ...game,
  phase: game.phase === 'guardianReady' ? 'ready' : 'rolled',
});

export const updateGuardianThrow = (game, operation) => {
  if (!['guardianReady', 'guardianRolled'].includes(game.phase)) return game;
  const proxy = getGuardianRollGame(game);
  const next = operation(proxy);
  return next === proxy ? game : { ...next, phase: game.phase };
};

export const beginGuardianRoll = (game, launch) => {
  if (game.phase !== 'guardianReady') return game;
  const next = beginRoomRoll(getGuardianRollGame(game), game.roomId);
  return next.phase === 'rolled'
    ? { ...next, phase: 'guardianRolled', slingshotLaunch: launch }
    : game;
};

export const settleGuardianRoll = (game, roll, index, requestId, faceValues) =>
  updateGuardianThrow(game, (current) =>
    commitRoomRoll(current, roll, index, Math.random, requestId, faceValues)
  );

export const getGuardianAttack = (game) =>
  Math.max(
    0,
    getEffectiveRoll(getGuardianRollGame(game), getGuardianRoom(game)) || 0
  );

export const startGuardianAttack = (game, random = Math.random) => {
  if (
    game.phase !== 'guardianRolled' ||
    !game.guardian?.active ||
    !Number.isInteger(game.roll) ||
    !Number.isInteger(game.landingIndex) ||
    needsBoardTileChoice(game) ||
    getBoardRelaunchRequest(game) ||
    game.lastStandProtocol?.pending
  )
    return game;
  const guardian = game.guardian;
  const room = getGuardianRoom(game);
  // The game transaction happens once. The scene presents this saved result;
  // replaying or skipping its animation can never apply damage or loot again.
  const resolved = resolveRoll(getGuardianRollGame(game), random, room);
  if (resolved.lastStandProtocol?.pending)
    return { ...resolved, phase: 'guardianRolled' };
  const result = resolved.lastResult;
  const enemyHpAfter = Math.max(
    0,
    guardian.hp - Math.max(0, result.effectiveRoll)
  );
  const consumed = getTriggeredBoardSquares(game)
    .filter(({ square }) =>
      ['heal', 'shards', 'relic'].includes(square.effect?.type)
    )
    .map(({ index }) => index);
  const lastRound = {
    ...result,
    target: guardian.maxHp,
    enemy: guardian.enemy,
    guardian: true,
    miniBoss: guardian.isWarden,
    attacks: guardian.round,
    round: guardian.round,
    combatId: game.floor + ':' + game.roomId + ':' + guardian.round,
    attackDealt: Math.min(guardian.hp, Math.max(0, result.effectiveRoll)),
    enemyHpBefore: guardian.hp,
    enemyHpAfter,
    enemyMaxHp: guardian.maxHp,
    playerHpBefore: game.hp,
    playerMaxHpBefore: getMaxHp(game),
    playerHpAfter: resolved.hp,
    playerMaxHp: resolved.maxHp,
    relicsBefore: [...game.relics],
    retaliation: enemyHpAfter > 0,
    lastStandResolvedAtImpact:
      game.lastStandProtocol?.lastAttempt?.rescuedAtImpact === true &&
      game.lastStandProtocol.lastAttempt.encounter ===
        getLastStandEncounter(game),
    counterDamage: result.retaliationDamage,
    damageCalculation: {
      initialRoll: getBaseRoll(game),
      flipped: !!game.flipped,
      boardRoll: getBoardAdjustedRoll(game),
      finalRoll: result.effectiveRoll,
      boardSteps: getBoardRollSteps(game, getBaseRoll(game)).map(
        ({ index, square, before, value }) => ({
          index,
          label: getBoardSquareLabel(square),
          before,
          value,
        })
      ),
      relicEffects: getRollRelicEffects(game),
      defenses: result.damagePrevention,
    },
    treasureRewards: getBoardTreasureRewards(game),
    loot: { ...result.loot, relicChoice: false },
  };
  return {
    ...resolved,
    phase: 'guardianStrike',
    lastResult: lastRound,
    history: result.success
      ? [lastRound, ...game.history].slice(0, 5)
      : game.history,
    guardian: {
      ...guardian,
      hp: enemyHpAfter,
      lastRound,
      consumed: [...new Set([...guardian.consumed, ...consumed])],
    },
  };
};

const clearThrow = (game) => ({
  ...game,
  roll: null,
  landingIndex: null,
  flipped: false,
  boardModifier: 0,
  boardTileResolution: null,
  powerUp: null,
  boardTreasureReward: null,
  boardTreasureRewards: [],
  chainReactionActivation: null,
  throwCombo: null,
  bountyTag: { targetIndex: null, attempt: null },
  slingshotLaunch: null,
  // Pickups are finite for a fight. Other tiles, including upgraded Power Up
  // tiles and the board's environmental condition, stay exactly where they are.
  doorBoard: game.doorBoard.map((square, index) =>
    game.guardian.consumed.includes(index) &&
    ['heal', 'shards', 'relic'].includes(square.effect?.type)
      ? { modifier: 20 }
      : square
  ),
});

export const finishGuardianAttack = (game) => {
  if (game.phase !== 'guardianStrike') return game;
  if (game.hp <= 0)
    return {
      ...game,
      phase: 'lost',
      guardian: { ...game.guardian, active: false },
      history: [game.guardian.lastRound, ...game.history].slice(0, 5),
    };
  if (game.guardian.hp === 0)
    return {
      ...game,
      phase: 'result',
      guardian: { ...game.guardian, active: false, defeated: true },
    };
  return migratePlayerBoardBalance({
    ...clearThrow(game),
    phase: 'guardianReady',
    lastResult: null,
    guardian: { ...game.guardian, round: game.guardian.round + 1 },
  });
};

const migrateSavedThrow = (before, next) => {
  const sameRound = next.guardian.round === (before.miniBoss?.round || 1);
  const oldEncounter = getLastStandEncounter(before);
  const newEncounter = getLastStandEncounter(next);
  const protocol = next.lastStandProtocol;
  const moveAttempt = (attempt) =>
    sameRound && attempt?.encounter === oldEncounter
      ? { ...attempt, encounter: newEncounter }
      : attempt;
  return {
    ...next,
    boardTileResolution: next.boardTileResolution
      ? {
          ...next.boardTileResolution,
          round: getBoardEncounterRound(next),
        }
      : null,
    throwCombo: next.throwCombo
      ? { ...next.throwCombo, encounter: newEncounter }
      : null,
    lastStandProtocol: protocol
      ? {
          ...protocol,
          pending: moveAttempt(protocol.pending),
          lastAttempt: moveAttempt(protocol.lastAttempt),
        }
      : protocol,
  };
};

// Existing runs keep their settled roll, board, wounds, and earned rewards.
export const migrateGuardianGame = (game) => {
  if (
    !game ||
    GUARDIAN_PHASES.includes(game.phase) ||
    game.floor <= 1 ||
    game.floor >= 10
  )
    return game;
  const room = getRooms(game).find((entry) => entry.id === game.roomId);
  if (!room) return game;
  if (MINI_BOSS_PHASES.includes(game.phase)) {
    const old = game.miniBoss;
    const guardian = {
      ...createGuardian(game, room),
      enemy: RIFT_WARDEN,
      hp: old.hp,
      round: old.round,
      consumed: old.consumed || [],
      lastRound: old.lastRound,
    };
    if (game.phase === 'minibossReward') {
      const id = old.offers[0] || null;
      // This legacy screen already paid 10 shards. Keep currency earned before
      // the rebalance rather than deducting it when the save is migrated.
      const legacyReward = Math.max(10, getWardenShardReward(old.round));
      const bonusShards = legacyReward - 10;
      const lastResult = {
        ...old.lastRound,
        attacks: old.round,
        guardian: true,
        earned: (old.lastRound?.earned || 10) + bonusShards,
        bonusRelic: id,
        loot: { shards: legacyReward, relic: id },
      };
      return {
        ...game,
        phase: 'result',
        miniBoss: null,
        shards: game.shards + bonusShards,
        totalShardsAcquired: acquireShards(game, bonusShards),
        relics:
          id && !game.relics.includes(id) ? [...game.relics, id] : game.relics,
        lastResult,
        guardian: { ...guardian, active: false, defeated: true },
      };
    }
    const next = {
      ...game,
      miniBoss: null,
      guardian,
      phase:
        game.phase === 'minibossRolled' ? 'guardianRolled' : 'guardianReady',
    };
    return migrateSavedThrow(
      game,
      game.phase === 'minibossResult'
        ? {
            ...clearThrow(next),
            guardian: { ...guardian, round: old.round + 1 },
            lastResult: null,
          }
        : next
    );
  }
  if (!['ready', 'rolled'].includes(game.phase)) return game;
  return migrateSavedThrow(game, {
    ...game,
    guardian: game.guardian || createGuardian(game, room),
    phase: game.phase === 'ready' ? 'guardianReady' : 'guardianRolled',
  });
};
